import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { ScrollToTop } from "../components/ScrollToTop";
import { api } from "../lib/api";
import { ContextPanel } from "../components/ContextPanel";
import { DocumentViewer } from "../components/DocumentViewer";
import { TranscriptionPane } from "../components/TranscriptionPane";
import { TeachingPanel } from "../components/TeachingPanel";

export default function Workspace() {
  const [images, setImages] = useState([]);
  const [imageNames, setImageNames] = useState([]);
  const [title, setTitle] = useState("");
  const [year, setYear] = useState("");
  const [region, setRegion] = useState("Noord-Holland");
  const [language, setLanguage] = useState("Nederlands");
  const [docType, setDocType] = useState("handgeschreven");
  const [frontImage, setFrontImage] = useState("");

  const [teachingText, setTeachingText] = useState([]);
  const [teachingExamples, setTeachingExamples] = useState([]);
  const [teachOpen, setTeachOpen] = useState(false);

  const [profiles, setProfiles] = useState([]);
  const [activeProfileId, setActiveProfileId] = useState(null);

  const [result, setResult] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const [editedText, setEditedText] = useState("");
  const baselineRef = useRef({ text: "", metaStr: "" });
  const originalAiTextRef = useRef("");
  const savedIdRef = useRef(null);
  const persistLockRef = useRef(Promise.resolve());
  const [meta, setMeta] = useState({ writer: "", writer_relation: "", date_text: "", date_iso: "", place: "", salutation: "", signature: "", summary: "", tone: "", is_typed: false, date_note: "", date_user_note: "" });
  const [liveText, setLiveText] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingSample, setLoadingSample] = useState(false);

  const activeProfile = profiles.find((p) => p.id === activeProfileId);

  useEffect(() => {
    api.listProfiles().then(setProfiles).catch(() => {});
  }, []);

  const addImages = (urls, names = []) => {
    setImages((prev) => [...prev, ...urls]);
    setImageNames((prev) => [...prev, ...urls.map((_, i) => names[i] || "")]);
  };
  const removeImage = (idx) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
    setImageNames((prev) => prev.filter((_, i) => i !== idx));
  };
  const reorderImages = (from, to) => {
    if (to < 0 || to >= images.length) return;
    setImages((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setImageNames((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const cleanRegion = () => (region === "Onbekend / n.v.t." ? "" : region);

  const loadSample = async (s) => {
    setLoadingSample(true);
    try {
      const { image_base64 } = await api.fetchImage(s.url);
      setImages([image_base64]);
      setImageNames([`${s.title} (voorbeeld)`]);
      setTitle(s.title);
      setYear(s.year);
      setRegion(s.region);
      setResult(null);
      setSavedId(null);
      savedIdRef.current = null;
      setEditedText("");
      toast.success(`Voorbeeld geladen: ${s.title}`);
    } catch (e) {
      toast.error("Kon voorbeeld niet laden");
    } finally {
      setLoadingSample(false);
    }
  };

  const runTranscribe = async () => {
    if (!images.length) {
      toast.error("Upload eerst een scan");
      return;
    }
    setLoading(true);
    setResult(null);
    setLiveText("");
    const base = {
      year,
      region: cleanRegion(),
      source_language: language || "Nederlands",
      doc_type: docType,
      teaching_text: teachingText,
      teaching_examples: teachingExamples,
      profile_id: activeProfileId || null,
    };
    // Each page is transcribed as a whole via a background job (no slicing).
    // Slicing used to be a workaround for the 60s gateway cap, but it cut handwriting
    // lines in half and hurt quality. The background-job + polling flow removes the cap,
    // so we now send each full page in one request for the best accuracy.
    try {
      const results = [];
      let combined = "";
      for (let p = 0; p < images.length; p++) {
        const label = `— Pagina ${p + 1}/${images.length} wordt gelezen… —`;
        setLiveText(`${combined}${combined ? "\n\n" : ""}${label}`);
        const d = await new Promise((resolve) => {
          api.jobTranscribe(
            { ...base, images: [images[p]] },
            {
              onPartial: (raw) => setLiveText(`${combined}${combined ? "\n\n" : ""}${label}\n${raw.split("%%%META%%%")[0]}`),
              onDone: (res) => resolve(res),
              onError: (msg) => resolve({ transcription: "", error: msg || "Transcriptie mislukt" }),
            }
          );
        });
        results.push(d);
        const t = (d.transcription || "").trim();
        if (t) combined += (combined ? "\n\n" : "") + t;
        setLiveText(combined);
      }
      if (!combined.trim()) {
        const firstError = results.find((r) => r.error)?.error;
        if (firstError) throw new Error(firstError);
        throw new Error("Geen leesbare tekst gevonden. Controleer of je de kant met de tekst hebt geüpload (bij een ansichtkaart: de achterzijde).");
      }
      const first = results.find((r) => r.date_text || r.place || r.salutation) || results[0] || {};
      const last = [...results].reverse().find((r) => r.signature) || results[results.length - 1] || {};
      const aiHighlights = [...new Set(results.flatMap((r) => r.highlights || []).map((h) => String(h).trim()).filter(Boolean))].slice(0, 6);
      const aiTitle = (first.title || results.find((r) => r.title)?.title || "").trim();
      setResult({
        transcription: combined,
        uncertain: results.flatMap((r) => r.uncertain || []),
        notes: first.notes || "",
        highlights: aiHighlights,
      });
      if (!title.trim() && aiTitle) setTitle(aiTitle);
      const newMeta = {
        writer: activeProfile?.name || (activeProfileId === "other" ? (meta.writer || "") : ""),
        writer_relation: activeProfileId === "other" ? (meta.writer_relation || "") : "",
        date_text: first.date_text || "",
        date_iso: first.date_iso || "",
        place: first.place || "",
        salutation: first.salutation || "",
        signature: last.signature || "",
        summary: first.summary || "",
        tone: first.tone || "",
        is_typed: docType === "getypt" || results.some((r) => r.is_typed),
        date_note: first.date_note || "",
      };
      setMeta(newMeta);
      const plain = combined.replace(/[⟦⟧]/g, "");
      setEditedText(plain);
      setSavedId(null);
      savedIdRef.current = null;
      baselineRef.current = { text: plain, metaStr: JSON.stringify(newMeta) };
      originalAiTextRef.current = plain;
      setLiveText("");
      setLoading(false);
      toast.success(`Transcriptie gereed${images.length > 1 ? ` (${images.length} pagina's)` : ""}`);
    } catch (e) {
      setLiveText("");
      setLoading(false);
      toast.error(e?.response?.data?.detail || e.message || "Transcriptie mislukt. Probeer het opnieuw.");
    }
  };

  const addHint = (hint) => setTeachingText((prev) => [...prev, hint]);

  const onLearnCorrection = (orig, val) => {
    const clean = (val || "").trim();
    if (!clean || clean === (orig || "").trim()) return;
    const hint = `Het handgeschreven woord dat eerst gelezen werd als "${orig}" is in werkelijkheid: "${clean}"`;
    setTeachingText((prev) => {
      if (prev.includes(hint)) return prev;
      const next = [...prev, hint];
      if (activeProfileId && activeProfile) {
        api
          .updateProfile(activeProfileId, {
            name: activeProfile.name,
            language,
            teaching_text: next,
            teaching_examples: teachingExamples,
          })
          .then((p) => {
            setProfiles((list) => list.map((x) => (x.id === p.id ? p : x)));
            toast.success(`Geleerd voor profiel "${activeProfile.name}"`);
          })
          .catch(() => {});
      }
      return next;
    });
  };

  const selectProfile = (id) => {
    if (id === "none") {
      setActiveProfileId(null);
      return;
    }
    if (id === "other") {
      setActiveProfileId("other");
      setTeachingText([]);
      setTeachingExamples([]);
      setMeta((m) => ({ ...m, writer: "", writer_relation: "" }));
      return;
    }
    const p = profiles.find((x) => x.id === id);
    if (!p) return;
    setActiveProfileId(id);
    setTeachingText(p.teaching_text || []);
    setTeachingExamples(p.teaching_examples || []);
    if (p.language) setLanguage(p.language);
    toast.success(`Profiel geladen: ${p.name}`);
  };

  const deleteProfile = async (id) => {
    const p = profiles.find((x) => x.id === id);
    if (!p) return;
    if (!window.confirm(`Profiel "${p.name}" verwijderen? Het geleerde handschrift en de voorbeelden van dit profiel gaan verloren. De opgeslagen brieven blijven bewaard.`)) return;
    try {
      await api.deleteProfile(id);
      setProfiles((prev) => prev.filter((x) => x.id !== id));
      if (activeProfileId === id) {
        setActiveProfileId(null);
        setTeachingText([]);
        setTeachingExamples([]);
      }
      toast.success(`Profiel "${p.name}" verwijderd`);
    } catch (e) {
      toast.error("Verwijderen mislukt");
    }
  };

  const saveNewProfile = async (name) => {
    try {
      const p = await api.createProfile({
        name,
        language,
        teaching_text: teachingText,
        teaching_examples: teachingExamples,
      });
      setProfiles((prev) => [p, ...prev]);
      setActiveProfileId(p.id);
      toast.success(`Profiel "${name}" opgeslagen`);
    } catch (e) {
      toast.error("Profiel opslaan mislukt");
    }
  };

  const updateActiveProfile = async () => {
    if (!activeProfile) return;
    try {
      const p = await api.updateProfile(activeProfile.id, {
        name: activeProfile.name,
        language,
        teaching_text: teachingText,
        teaching_examples: teachingExamples,
      });
      setProfiles((prev) => prev.map((x) => (x.id === p.id ? p : x)));
      toast.success(`Profiel "${p.name}" bijgewerkt`);
    } catch (e) {
      toast.error("Bijwerken mislukt");
    }
  };

  const onExport = async (format, text) => {
    try {
      await api.exportFile(format, {
        title: title.trim() || "Transcriptie",
        year,
        region: cleanRegion(),
        source_language: language,
        writer: meta.writer || "",
        date_text: meta.date_text || "",
        place: meta.place || "",
        images,
        transcription: text,
        notes: result?.notes || "",
      });
      toast.success(`Geëxporteerd als ${format.toUpperCase()}`);
    } catch (e) {
      toast.error("Export mislukt");
    }
  };

  const save = async (finalText) => {
    if (!result) return;
    setSaving(true);
    await persist(finalText, { silent: false });
    setSaving(false);
  };

  const persist = (text, { silent } = {}) => {
    if (!result) return Promise.resolve();
    const run = async () => {
      try {
        if (savedIdRef.current) {
          // Lichte update: alleen tekst + metadata (geen afbeeldingen opnieuw versturen).
          await api.updateLetterMeta(savedIdRef.current, {
            title: title.trim(),
            writer: meta.writer || "",
            writer_relation: meta.writer_relation || "",
            date_text: meta.date_text || "",
            place: meta.place || "",
            salutation: meta.salutation || "",
            signature: meta.signature || "",
            summary: meta.summary || "",
            tone: meta.tone || "",
            date_note: meta.date_note || "",
            date_user_note: meta.date_user_note || "",
            transcription: text,
            notes: result.notes || "",
            highlights: result.highlights || [],
          });
        } else {
          const created = await api.createLetter({
            title: title.trim(),
            year,
            region: cleanRegion(),
            source_language: language || "Nederlands",
            writer: meta.writer || "",
            writer_relation: meta.writer_relation || "",
            date_text: meta.date_text || "",
            date_iso: meta.date_iso || "",
            place: meta.place || "",
            salutation: meta.salutation || "",
            signature: meta.signature || "",
            summary: meta.summary || "",
            tone: meta.tone || "",
            doc_type: docType,
            is_typed: !!meta.is_typed,
            front_image: frontImage || "",
            date_note: meta.date_note || "",
            date_user_note: meta.date_user_note || "",
            images,
            teaching_text: teachingText,
            teaching_examples: teachingExamples,
            transcription: text,
            uncertain: result.uncertain || [],
            notes: result.notes || "",
            highlights: result.highlights || [],
            profile_id: (activeProfileId && activeProfileId !== "other") ? activeProfileId : null,
          });
          savedIdRef.current = created.id;
          setSavedId(created.id);
        }
        baselineRef.current = { text, metaStr: JSON.stringify(meta) };
        // Leer van ALLE correcties (ook buiten de gemarkeerde woorden): vergelijk de
        // oorspronkelijke AI-tekst met de huidige tekst en sla de verschillen op als leerregels.
        if (activeProfileId && originalAiTextRef.current && text && text !== originalAiTextRef.current) {
          try {
            const updated = await api.learnFromCorrection(activeProfileId, originalAiTextRef.current, text);
            setProfiles((list) => list.map((x) => (x.id === updated.id ? updated : x)));
            setTeachingText(updated.teaching_text || []);
          } catch (e) { /* stil: leren mag het opslaan nooit blokkeren */ }
        }
        if (!silent) toast.success("Opgeslagen in Brievenboek");
      } catch (e) {
        if (!silent) toast.error("Opslaan mislukt");
      }
    };
    // Serialiseer alle opslag-acties zodat auto-save + handmatig opslaan nooit
    // tegelijk een nieuwe brief aanmaken (voorkomt dubbele brieven).
    const p = persistLockRef.current.then(run, run);
    persistLockRef.current = p;
    return p;
  };

  // Auto-save: sla wijzigingen automatisch op (gedebounced) zodat je nooit aanpassingen verliest.
  useEffect(() => {
    if (!result) return;
    const dirty = editedText !== baselineRef.current.text || JSON.stringify(meta) !== baselineRef.current.metaStr;
    if (!dirty) return;
    const h = setTimeout(() => { persist(editedText, { silent: true }); }, 1500);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedText, meta, result]);

  return (
    <main className="mx-auto max-w-[1500px] px-4 py-6 sm:px-8">
      <div className="mb-5">
        <h1 className="font-serif text-3xl font-bold tracking-tight text-[var(--ink)] sm:text-4xl">
          Handgeschreven brief transcriberen
        </h1>
        <p className="mt-1.5 max-w-2xl text-sm text-[var(--ink-2)] sm:text-base">
          Upload een scan, geef context en leer de AI het handschrift lezen. Onduidelijke passages worden
          gemarkeerd zodat jij ze kunt corrigeren.
        </p>
      </div>

      <ContextPanel
        title={title} setTitle={setTitle}
        year={year} setYear={setYear}
        region={region} setRegion={setRegion}
        language={language} setLanguage={setLanguage}
        docType={docType} setDocType={setDocType}
        frontImage={frontImage} onFrontImage={setFrontImage} onRemoveFront={() => setFrontImage("")}
        onTranscribe={runTranscribe}
        loading={loading}
        canTranscribe={images.length > 0}
        onOpenTeaching={() => setTeachOpen(true)}
        teachingCount={teachingText.length + teachingExamples.length}
        onLoadSample={loadSample}
        loadingSample={loadingSample}
        profiles={profiles}
        activeProfileId={activeProfileId}
        onSelectProfile={selectProfile}
        onDeleteProfile={deleteProfile}
      />

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="h-[72vh] overflow-hidden rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface)] shadow-sm">
          <DocumentViewer images={images} names={imageNames} onImages={addImages} onRemove={removeImage} onReorder={reorderImages} />
        </div>
        <div className="h-[72vh] overflow-hidden rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface)] shadow-sm">
          <TranscriptionPane
            result={result}
            loading={loading}
            liveText={liveText}
            meta={meta}
            setMeta={setMeta}
            onAddHint={addHint}
            onLearnCorrection={onLearnCorrection}
            onRetranscribe={runTranscribe}
            onSave={save}
            onTextChange={setEditedText}
            autoSaved={!!savedId}
            saving={saving}
            hasImages={images.length > 0}
            onExport={onExport}
          />
        </div>
      </div>

      <TeachingPanel
        open={teachOpen}
        onOpenChange={setTeachOpen}
        teachingText={teachingText}
        setTeachingText={setTeachingText}
        teachingExamples={teachingExamples}
        setTeachingExamples={setTeachingExamples}
        onSaveNewProfile={saveNewProfile}
        onUpdateActiveProfile={updateActiveProfile}
        activeProfileName={activeProfile?.name}
      />
      <ScrollToTop />
    </main>
  );
}
